<template>
  <span
    class="base-badge"
    :class="[variant, size, align, color, rounded ? 'rounded' : undefined]"
    :data-testid="testId"
  >
    <BaseIcon
      v-if="icon"
      :id="icon"
      :size="iconSize"
    />
    <slot>{{ label }}</slot>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import BaseIcon from './BaseIcon.vue';

/**
 * Available colors from design system
 */
export type BadgeColor = 'dark' | 'yellow' | 'gray' | 'blue' | 'red' | 'amber' | 'green' | 'teal' | 'purple' | 'pink';

/**
 * Props interface for BaseBadge component
 */
export interface BaseBadgeProps {
  /**
   * Text content to display in the badge
   * @default undefined
   */
  label?: string;

  /**
   * Visual style variant of the badge
   * - 'outline': Light background with colored border and text
   * - 'solid': Solid background with white text
   * @default 'outline'
   */
  variant?: 'solid' | 'outline';

  /**
   * Size of the badge, affecting padding and font size
   * - 'sm': Compact size for dense layouts
   * - 'md': Standard size for general use
   * - 'lg': Prominent size for emphasis
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Horizontal alignment of the badge within its container
   * - 'left': Align to the left (default flow)
   * - 'center': Center the badge with auto margins
   * - 'right': Align to the right with left auto margin
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right';

  /**
   * Optional icon identifier to display before the text content
   * Icon size automatically adjusts based on badge size
   * @example 'check', 'warning', 'close', 'info', 'bullet'
   */
  icon?: string;

  /**
   * Direct color from design system
   * @example 'dark' | 'blue' | 'green' | 'red' | 'amber'
   */
  color?: BadgeColor;

  /**
   * Test identifier for automated testing
   * @example 'status-badge', 'plan-badge'
   */
  testId?: string;

  /** Makes badge fully rounded */
  rounded?: boolean;
}

const props = withDefaults(defineProps<BaseBadgeProps>(), {
  variant: 'outline',
  size: 'md',
  align: 'left',
  label: undefined,
  icon: undefined,
  color: undefined,
  testId: undefined,
  rounded: undefined,
});

const ICON_SIZE_MAP = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
} as const;

const iconSize = computed(() => ICON_SIZE_MAP[props.size] || 'md');
</script>

<style>
.base-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--badge-gap);
  border-radius: var(--border-radius-sm);
  padding-block: var(--badge-padding-block-md);
  padding-inline: var(--badge-padding-inline-md);
  width: fit-content;
  font-weight: var(--font-semi);
  font-family: var(--font-body);
  border: 1px solid var(--badge-border-color, var(--color-neutral-400));
  background-color: var(--badge-background-color, var(--color-light));
  color: var(--badge-text-color, var(--color-dark));
  transition: var(--transition-default);

  /* Size variants */
  &.sm {
    font-size: var(--font-sm);
    padding-block: var(--badge-padding-block-sm);
    padding-inline: var(--badge-padding-inline-sm);
  }

  &.md {
    font-size: var(--font-md);
    padding-block: var(--badge-padding-block-md);
    padding-inline: var(--badge-padding-inline-md);
  }

  &.lg {
    font-size: var(--font-lg);
    padding-block: var(--badge-padding-block-lg);
    padding-inline: var(--badge-padding-inline-lg);
  }

  /* Style variants */
  &.solid {
    color: var(--color-light);
    background-color: var(--badge-background-color);
    border-color: var(--badge-border-color);
  }

  &.outline {
    background-color: var(--badge-background-light);
    border-color: var(--badge-border-color);
    color: var(--badge-text-color);
  }

  &.rounded {
    border-radius: var(--border-radius-full);
    padding-inline: var(--badge-padding-inline-lg);
  }

  /* Alignment */
  &.center {
    margin: 0 auto;
  }

  &.right {
    margin-left: auto;
  }

  /* Direct color support from design system */
  &.dark {
    --badge-background-color: var(--color-dark);
    --badge-background-light: var(--color-light);
    --badge-border-color: var(--color-dark);
    --badge-text-color: var(--color-dark);
  }

  &.yellow {
    --badge-background-color: var(--color-yellow-400);
    --badge-background-light: var(--color-yellow-100);
    --badge-border-color: var(--color-yellow-400);
    --badge-text-color: var(--color-yellow-400);
  }

  &.gray {
    --badge-background-color: var(--color-neutral-400);
    --badge-background-light: var(--color-neutral-100);
    --badge-border-color: var(--color-neutral-400);
    --badge-text-color: var(--color-neutral-400);
  }

  &.blue {
    --badge-background-color: var(--color-blue-400);
    --badge-background-light: var(--color-blue-100);
    --badge-border-color: var(--color-blue-400);
    --badge-text-color: var(--color-blue-400);
  }

  &.red {
    --badge-background-color: var(--color-red-400);
    --badge-background-light: var(--color-red-100);
    --badge-border-color: var(--color-red-400);
    --badge-text-color: var(--color-red-400);
  }

  &.amber {
    --badge-background-color: var(--color-amber-400);
    --badge-background-light: var(--color-amber-100);
    --badge-border-color: var(--color-amber-400);
    --badge-text-color: var(--color-amber-400);
  }

  &.green {
    --badge-background-color: var(--color-green-400);
    --badge-background-light: var(--color-green-100);
    --badge-border-color: var(--color-green-400);
    --badge-text-color: var(--color-green-400);
  }

  &.teal {
    --badge-background-color: var(--color-teal-400);
    --badge-background-light: var(--color-teal-100);
    --badge-border-color: var(--color-teal-400);
    --badge-text-color: var(--color-teal-400);
  }

  &.purple {
    --badge-background-color: var(--color-purple-400);
    --badge-background-light: var(--color-purple-100);
    --badge-border-color: var(--color-purple-400);
    --badge-text-color: var(--color-purple-400);
  }

  &.pink {
    --badge-background-color: var(--color-pink-400);
    --badge-background-light: var(--color-pink-100);
    --badge-border-color: var(--color-pink-400);
    --badge-text-color: var(--color-pink-400);
  }
}
</style>
